import {Button, createStyles, Divider, Flex, Group, Stack, Text, UnstyledButton} from '@mantine/core';
import React from 'react';

import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import StorableChip from 'inventory/chips/StorableChip';
import {GetScratchPlateCreatedPlates_scratchPlate_ScratchPlateNode_createdPlates as ScratchPlateCreatedPlate} from 'notebook/integrations/plate-maps/scratch-plates/__generated/GetScratchPlateCreatedPlates';
import {SCRATCH_PLATE_VIEWER_MIN_HEIGHT} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewConstants';
import ScratchPlateViewer from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewer';
import useFilledScratchPlateData from 'notebook/integrations/plate-maps/scratch-plates/useFilledScratchPlateData';
import {openPutScratchPlateInInventoryModal} from 'samples/plate-maps/scratch-plates/PutScratchPlateInInventoryModal';
import {CollapsibleSidebar} from 'samples/plate-maps/sidebar/CollapsibleSidebar';
import {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {DATETIME_FORMATS, PYTHON_ISOFORMAT_WITH_TIMEZONE} from 'util/DateConstants';
import {formatDatetimeValue, isValidDateOrDatetimeOrTime} from 'util/DateUtil';

// `createdAt` comes from Python `datetime.isoformat()` (microsecond precision),
// which DATETIME_FORMATS doesn't cover, so add the microsecond format.
const CREATED_AT_FORMATS = [...DATETIME_FORMATS, PYTHON_ISOFORMAT_WITH_TIMEZONE];

export type RecordsListItem =
  | {
      kind: 'plate';
      id: string;
      name: string | null;
      createdAt: string | null;
      inventoryPlateId: string | null;
    }
  | {
      kind: 'inaccessible';
      id: string;
      invalidId: string;
    };

export function buildScratchPlateRecordsItems(
  createdPlates: Array<ScratchPlateCreatedPlate>
): Array<RecordsListItem> {
  // Backend returns created plates newest-first; preserve that order.
  return createdPlates.flatMap((createdPlate): Array<RecordsListItem> => {
    if (createdPlate.__typename === 'ScratchPlateNode') {
      return [
        {
          kind: 'plate',
          id: createdPlate.id,
          name: createdPlate.name,
          createdAt: createdPlate.createdAt,
          inventoryPlateId:
            createdPlate.inventoryPlate != null
              ? getAccessibleOrInaccessibleId(createdPlate.inventoryPlate)
              : null,
        },
      ];
    }
    if (isInaccessible(createdPlate)) {
      return [
        {
          kind: 'inaccessible',
          id: `inaccessible-${createdPlate.invalidId}`,
          invalidId: createdPlate.invalidId,
        },
      ];
    }
    return [];
  });
}

function formatCreatedAt(createdAt: string | null): string {
  if (createdAt == null) {
    return 'Created date unavailable';
  }

  if (!isValidDateOrDatetimeOrTime({textValue: createdAt, validFormats: CREATED_AT_FORMATS})) {
    return 'Created date unavailable';
  }

  const formattedCreatedAt = formatDatetimeValue(createdAt, CREATED_AT_FORMATS, 'lll');
  return `Created ${formattedCreatedAt}`;
}

function RecordsDetailView({
  scratchPlateId,
  scratchPlateName,
  inventoryPlateId,
}: {
  scratchPlateId: string;
  scratchPlateName: string | null;
  inventoryPlateId: string | null;
}): JSX.Element {
  const {classes} = useStyles();
  const {result, plate} = useFilledScratchPlateData(scratchPlateId);
  const displayName = plate?.name ?? scratchPlateName ?? scratchPlateId;

  function handlePutInInventory(): void {
    if (plate == null) {
      return;
    }
    openPutScratchPlateInInventoryModal({
      scratchPlateId,
      scratchPlateName: plate.name,
      plateSize: [plate.height, plate.width],
      plateSchemaId: plate.plateSchemaId,
    });
  }

  return (
    <Flex direction="column" h="100%" w="100%">
      <Group position="apart" align="center" spacing={8}>
        <Text className={classes.recordsDetailTitle}>{`Viewing ${displayName}`}</Text>
        {inventoryPlateId != null ? (
          <Group spacing={6}>
            <StorableChip id={inventoryPlateId} size={ChipSize.SMALL} />
          </Group>
        ) : (
          <Button
            size="sm"
            leftIcon={<BenchlingIcon icon="box-arrow-right" />}
            variant="primary"
            disabled={plate == null}
            onClick={handlePutInInventory}
          >
            Put in inventory
          </Button>
        )}
      </Group>
      <Flex direction="column" mih={0} sx={{flex: 1}} w="100%">
        <div className={classes.plateViewerWrapper}>
          <ScratchPlateViewer result={result} />
        </div>
      </Flex>
    </Flex>
  );
}

interface ScratchPlateRecordsViewProps {
  recordsItems: Array<RecordsListItem>;
  selectedPlateId: string | null;
  onSelectPlateId: (id: string) => void;
  viewToggle?: React.ReactNode;
  /** When provided, renders a "Fill additional plates" button below the list. */
  onFillAdditionalPlates?: () => void;
  fillAdditionalPlatesDisabled?: boolean;
}

export default function ScratchPlateRecordsView({
  recordsItems,
  selectedPlateId,
  onSelectPlateId,
  viewToggle,
  onFillAdditionalPlates,
  fillAdditionalPlatesDisabled = false,
}: ScratchPlateRecordsViewProps): JSX.Element {
  const {classes, cx} = useStyles();
  const selectedPlateItem =
    selectedPlateId == null
      ? null
      : (recordsItems.find(
          (item): item is Extract<RecordsListItem, {kind: 'plate'}> =>
            item.kind === 'plate' && item.id === selectedPlateId
        ) ?? null);

  return (
    <Flex sx={{flex: 1}} miw={0}>
      <CollapsibleSidebar component="ScratchPlateRecordsSidebar">
        <Stack h="100%" spacing={8}>
          {viewToggle != null && <div className={classes.stickyToggleContainer}>{viewToggle}</div>}
          <Stack className={classes.recordsSidebarList} spacing={8}>
            {recordsItems.map((item) =>
              item.kind === 'plate' ? (
                <div
                  key={item.id}
                  className={cx(classes.recordsSidebarRow, {
                    [classes.recordsSidebarRowActive]: selectedPlateId === item.id,
                  })}
                >
                  <div className={classes.recordsSidebarRowContent}>
                    <UnstyledButton
                      onClick={() => {
                        onSelectPlateId(item.id);
                      }}
                      aria-pressed={selectedPlateId === item.id}
                      className={cx(classes.recordsSidebarSelectButton, {
                        [classes.recordsSidebarSelectButtonWithChip]: item.inventoryPlateId != null,
                      })}
                    >
                      <Stack spacing={2}>
                        <Text fw={600}>{item.name ?? item.id}</Text>
                        <Text className={classes.recordsSidebarRowMeta}>
                          {formatCreatedAt(item.createdAt)}
                        </Text>
                      </Stack>
                    </UnstyledButton>
                    {item.inventoryPlateId != null && (
                      <div className={classes.inventoryChipInline}>
                        <StorableChip id={item.inventoryPlateId} size={ChipSize.SMALL} />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <UnstyledButton
                  key={item.id}
                  disabled={true}
                  className={cx(classes.recordsSidebarRow, classes.recordsSidebarRowDisabled)}
                >
                  <Stack spacing={2}>
                    <Text fw={600}>Inaccessible plate</Text>
                    <Text className={classes.recordsSidebarRowMeta}>{item.invalidId}</Text>
                  </Stack>
                </UnstyledButton>
              )
            )}
          </Stack>
          {onFillAdditionalPlates != null && (
            <div className={classes.fillButtonContainer}>
              <Button
                size="sm"
                leftIcon={<BenchlingIcon icon="fill-grid" />}
                variant="tertiary"
                fullWidth={true}
                disabled={fillAdditionalPlatesDisabled}
                onClick={onFillAdditionalPlates}
              >
                Fill additional plates
              </Button>
            </div>
          )}
        </Stack>
      </CollapsibleSidebar>
      <Divider orientation="vertical" className={classes.recordsDivider} />
      <Flex direction="column" sx={{flex: 1}} miw={0} className={classes.recordsPlatePane}>
        {selectedPlateId == null ? (
          // Keeps the empty state centered when the parent has no fixed height.
          <Flex justify="center" align="center" mih={SCRATCH_PLATE_VIEWER_MIN_HEIGHT} h="100%">
            <Text className={classes.emptyDetail}>Select a plate to view details.</Text>
          </Flex>
        ) : (
          <RecordsDetailView
            key={selectedPlateId}
            scratchPlateId={selectedPlateId}
            scratchPlateName={selectedPlateItem?.name ?? null}
            inventoryPlateId={selectedPlateItem?.inventoryPlateId ?? null}
          />
        )}
      </Flex>
    </Flex>
  );
}

const useStyles = createStyles((theme) => ({
  stickyToggleContainer: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: theme.white,
    paddingRight: theme.spacing.md,
    paddingBottom: theme.spacing.xs,
  },
  recordsSidebarList: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  fillButtonContainer: {
    borderTop: `1px solid ${theme.colors.gray[3]}`,
    paddingTop: theme.spacing.sm,
    paddingRight: theme.spacing.md,
  },
  recordsDivider: {
    margin: `-${theme.spacing.md} ${theme.spacing.md} -${theme.spacing.md} 0`,
    borderColor: theme.colors.gray[3],
  },
  recordsPlatePane: {
    overflow: 'auto',
  },
  recordsSidebarRow: {
    width: '100%',
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    background: theme.white,
    paddingBlock: theme.spacing.sm,
    paddingInline: theme.spacing.md,
  },
  recordsSidebarSelectButton: {
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
    cursor: 'pointer',
  },
  recordsSidebarSelectButtonWithChip: {
    flex: '0 1 28%',
    minWidth: 88,
  },
  recordsSidebarRowContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  recordsSidebarRowActive: {
    background: theme.colors.blue[0],
    borderColor: theme.colors.blue[4],
  },
  recordsSidebarRowDisabled: {
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  recordsSidebarRowMeta: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.xs,
  },
  inventoryChipInline: {
    flex: '1 1 72%',
    minWidth: 0,
    maxWidth: '72%',
    display: 'flex',
    justifyContent: 'flex-end',
    overflow: 'hidden',
    '& .chip': {
      maxWidth: '100%',
    },
  },
  recordsDetailTitle: {
    color: theme.colors.gray[7],
    fontWeight: 600,
  },
  emptyDetail: {
    color: theme.colors.gray[5],
  },
  plateViewerWrapper: {
    width: '100%',
    height: '100%',
    // PlateMapView sets minWidth inline on the .plate-map SVG, so !important is
    // needed to let the embedded viewer shrink to fit the detail pane.
    '.plate-map': {
      minWidth: '0 !important',
      width: '100%',
    },
    '.plate-map-legend': {
      width: '100%',
    },
  },
}));
